/**
 * Orquestación de una búsqueda: planificar → buscar → clasificar por lotes → veredicto.
 *
 * Se ejecuta en el navegador y llama al Worker a trozos pequeños, porque el plan
 * gratuito de Workers limita cada invocación a 10 ms de CPU y 50 peticiones externas.
 * La API es inyectable para poder probarla sin red.
 */

import { assess } from "./evidence";
import { type Lang, num, t } from "./i18n";
import { looksSpanish, translationKeepsTerms } from "./language";
import {
  ANSWER_LABELS,
  ApiError,
  type AnalysisResult,
  type AnswerKey,
  type ClassifyRequest,
  type ClassifyResponse,
  type Paper,
  type PaperResult,
  type ExpandRequest,
  type ExpandResponse,
  type Plan,
  type PlanRequest,
  type ReferencesPage,
  type ReferencesRequest,
  type SearchPage,
  type SearchRequest,
  type SortOrder,
  type SourceName,
  type StoredSearch,
  ARXIV_FIELDS,
  DEFAULT_SOURCES,
  SOURCE_MIN_INTERVAL_MS,
  SOURCE_QUICK_PAGES,
  SOURCE_SORTS,
} from "./models";
import { Deduper } from "./dedupe";
import { prioritize } from "./priority";
import { termForms } from "./query";
import { plainSummary } from "./summary";

export interface Api {
  plan(req: PlanRequest): Promise<Plan>;
  search(req: SearchRequest): Promise<SearchPage>;
  classify(req: ClassifyRequest): Promise<ClassifyResponse>;
  references(req: ReferencesRequest): Promise<ReferencesPage>;
  expand(req: ExpandRequest): Promise<ExpandResponse>;
  // Búsqueda guardada (caché, #12); sin ella no se reutilizan búsquedas
  stored?(id: string): Promise<StoredSearch>;
}

// Búsqueda ampliada con temas de los papers relevantes (pseudo-relevance feedback)
export const EXPANSION_MAX = 300;
const EXPANSION_TOPICS = 12;
const EXPANSION_MIN_COUNT = 3;
// Encabezados MeSH y palabras clave demasiado genéricos para ampliar una búsqueda
const GENERIC_TOPICS = new Set([
  "humans", "animals", "male", "female", "adult", "aged", "aged, 80 and over", "middle aged", "child",
  "child, preschool", "adolescent", "infant", "infant, newborn", "young adult", "pregnancy", "mice", "rats",
  "cross-sectional studies", "prospective studies", "retrospective studies", "cohort studies",
  "case-control studies", "follow-up studies", "longitudinal studies", "risk factors", "risk assessment",
  "surveys and questionnaires", "questionnaires", "treatment outcome", "time factors", "incidence",
  "prevalence", "odds ratio", "logistic models", "multivariate analysis", "reference values", "sex factors",
  "age factors", "double-blind method", "randomized controlled trials as topic", "meta-analysis as topic",
  "systematic reviews as topic", "united states", "china", "japan", "europe", "meta-analysis",
  "systematic review", "randomized controlled trial", "cohort study", "cross-sectional study", "medicine",
  "biology", "psychology", "public health", "epidemiology",
  // diseños, poblaciones y disciplinas: no son el tema de la pregunta (vistos en producción)
  "clinical trial", "clinical trials", "clinical trials as topic", "pediatrics", "paediatrics", "children",
  "adults", "women", "men", "elderly", "nutrition", "diet", "health", "disease", "therapy", "treatment",
  "placebo", "placebos", "evidence-based medicine", "complementary therapies", "review", "outcome",
]);

/** Temas indexados que más se repiten en los papers relevantes (sin genéricos ni los ya buscados). */
/**
 * Consulta con los temas del PRF añadidos al grupo de su concepto:
 * "(sugar OR sugars) AND (hyperactivity)" + ADHD → hyperactivity ⇒ "(sugar OR sugars) AND (hyperactivity OR adhd)".
 * Las formas invertidas de MeSH se dan la vuelta ("calcium, dietary" → "dietary calcium").
 */
export function addToGroups(groups: string[], assigned: { topic: string; group: number; synonyms?: string[] }[]): string | null {
  if (!assigned.some((a) => a.group >= 0 && a.group < groups.length)) return null;
  const clauses = groups.map((g, i) => {
    const extra = assigned
      .filter((a) => a.group === i)
      .flatMap((a) => [a.topic, ...(a.synonyms ?? [])])
      .map((t) => (t.split(",").length === 2 ? t.split(/,\s*/).reverse().join(" ") : t))
      .flatMap(termForms);
    if (!extra.length) return g;
    const inner = /^\(.*\)$/.test(g) ? g.slice(1, -1) : g;
    return `(${[...new Set([inner, ...extra])].join(" OR ")})`;
  });
  return clauses.join(" AND ");
}

export function frequentTopics(results: PaperResult[], alreadySearched: string): string[] {
  const searched = alreadySearched.toLowerCase();
  const counts = new Map<string, number>();
  for (const r of results) {
    if (!r.classification || r.classification.answer === "not_addressed") continue;
    for (const k of new Set((r.paper.keywords ?? []).map((x) => x.toLowerCase().trim()))) {
      if (!k || k.length > 60 || GENERIC_TOPICS.has(k) || searched.includes(k)) continue;
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, n]) => n >= EXPANSION_MIN_COUNT)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, EXPANSION_TOPICS)
    .map(([k]) => k);
}

// Revisiones cuyas referencias se siguen en la exhaustiva, y tope de papers por esa vía
export const SNOWBALL_REVIEWS = 5;
export const SNOWBALL_MAX = 300;
export const VIA_REVIEW = "Citado por una revisión sistemática";

// Si la consulta estricta da menos que esto, se completa con consultas más relajadas
export const MIN_RESULTS = 20;

export interface Progress {
  stage: "planning" | "searching" | "classifying" | "exhaustive" | "done";
  found: number;
  classified: number;
}

export interface AnalyzeOptions {
  question: string;
  query?: string;
  limit: number;
  backend: string;
  titleAbsOnly?: boolean;
  pageSize?: number;
  batchSize?: number;
  // Lotes de clasificación en paralelo (cada lote = una invocación del Worker)
  concurrency?: number;
  // Traductor del navegador (MyMemory con la IP del usuario); si falla, traduce el Worker
  translate?: (question: string) => Promise<string>;
  // Fuentes a consultar (por defecto, todas)
  sources?: SourceName[];
  paceMs?: Partial<Record<SourceName, number>>;
  signal?: AbortSignal;
  onProgress?: (p: Progress) => void;
  // Resultado parcial tras cada lote clasificado
  onUpdate?: (r: AnalysisResult) => void;
  // true: no reutilizar una búsqueda guardada («Actualizar»)
  refresh?: boolean;
  // Idioma de los avisos y del resumen (#16)
  lang?: Lang;
  // Token de Turnstile para empezar una búsqueda nueva (#14)
  turnstileToken?: () => Promise<string | undefined>;
}

export function summarize(results: PaperResult[]): Record<AnswerKey, number> {
  const summary = Object.fromEntries(Object.keys(ANSWER_LABELS).map((k) => [k, 0])) as Record<AnswerKey, number>;
  for (const r of results) {
    const answer = r.classification?.answer as AnswerKey | undefined;
    if (answer && answer in summary) summary[answer] += 1;
  }
  return summary;
}

/** La evidencia que más pesa primero; luego los que no responden, por relevancia; errores al final. */
export function sortResults(results: PaperResult[]): PaperResult[] {
  const key = (r: PaperResult): [number, number] => {
    const c = r.classification;
    if (!c) return [2, 0];
    return c.answer !== "not_addressed" ? [0, -c.weight] : [1, -c.relevance];
  };
  return [...results].sort((a, b) => {
    const [ka, va] = key(a);
    const [kb, vb] = key(b);
    return ka - kb || va - vb;
  });
}

/**
 * Alterna elementos de varias listas, sin duplicados: el mismo paper en varias fuentes
 * cuenta una vez, con los datos de todas fusionados (ver dedupe.ts).
 */
export function interleave(...lists: Paper[][]): Paper[] {
  const dedup = new Deduper();
  const out: Paper[] = [];
  const longest = Math.max(0, ...lists.map((l) => l.length));
  for (let i = 0; i < longest; i++) {
    for (const list of lists) {
      const p = list[i];
      const canonical = p && dedup.add(p);
      if (canonical) out.push(canonical);
    }
  }
  return out;
}

function checkAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException("Búsqueda cancelada", "AbortError");
}

interface SearchOpts {
  // Idioma de los avisos para el usuario
  lang?: Lang;
  titleAbsOnly?: boolean;
  pageSize?: number;
  signal?: AbortSignal;
  sources?: SourceName[];
  // Pausa mínima entre páginas por fuente (por defecto SOURCE_MIN_INTERVAL_MS)
  paceMs?: Partial<Record<SourceName, number>>;
}

/** Pausas entre páginas; los tests la sustituyen para no esperar. */
export const pacing = {
  sleep: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
};

/** Devuelve una función que espera lo necesario para respetar el ritmo de la fuente. */
function pacer(source: SourceName, paceMs = SOURCE_MIN_INTERVAL_MS) {
  const interval = paceMs[source] ?? 0;
  let last = 0;
  return async () => {
    if (!interval) return;
    const wait = last + interval - Date.now();
    if (last && wait > 0) await pacing.sleep(wait);
    last = Date.now();
  };
}

/** Pagina una fuente hasta reunir `limit` papers o agotarla. */
export async function searchSorted(
  api: Api,
  query: string,
  limit: number,
  sort: SortOrder,
  opts: SearchOpts & { source?: SourceName; maxPages?: number } = {},
): Promise<{ papers: Paper[]; hitCount: number }> {
  const papers: Paper[] = [];
  let hitCount = 0;
  let cursor: string | undefined;
  const source = opts.source ?? "europepmc";
  const pace = pacer(source, opts.paceMs);
  for (let pages = 0; papers.length < limit && pages < (opts.maxPages ?? Infinity); pages++) {
    checkAborted(opts.signal);
    await pace();
    const page = await api.search({
      query,
      sort,
      source,
      cursor,
      page_size: opts.pageSize,
      title_abs_only: opts.titleAbsOnly ?? true,
    });
    hitCount = page.hit_count;
    papers.push(...page.papers);
    if (!page.next_cursor) break;
    cursor = page.next_cursor;
  }
  return { papers: papers.slice(0, limit), hitCount };
}

/**
 * Mezcla, de cada fuente, los más citados (los estudios de referencia suelen ser
 * antiguos) con su orden por defecto (Europe PMC: relevancia muy sesgada a lo reciente;
 * OpenAlex: relevancia). Si una fuente falla, se sigue con las demás.
 */
export async function fetchMixed(
  api: Api,
  query: string,
  limit: number,
  opts: SearchOpts = {},
  notices: string[] = [],
): Promise<{ papers: Paper[]; total: number }> {
  const sources = opts.sources ?? DEFAULT_SOURCES;
  // Cada lista pide el límite completo: si una fuente falla o trae pocos, las demás completan
  const jobs = (["cited", "default"] as const).flatMap((sort) =>
    sources.filter((source) => SOURCE_SORTS[source].includes(sort)).map((source) => ({ sort, source })),
  );
  const settled = await Promise.allSettled(
    jobs.map(({ sort, source }) => searchSorted(api, query, limit, sort, { ...opts, source, maxPages: SOURCE_QUICK_PAGES[source]?.[sort] })),
  );
  const lists: Paper[][] = [];
  const hits: Partial<Record<SourceName, number>> = {};
  settled.forEach((r, i) => {
    const job = jobs[i]!;
    if (r.status === "fulfilled") {
      lists.push(r.value.papers);
      hits[job.source] = Math.max(hits[job.source] ?? 0, r.value.hitCount);
    } else if ((r.reason as Error)?.name === "AbortError") {
      throw r.reason;
    } else if (!notices.some((n) => n.includes(sourceLabel(job.source)))) {
      notices.push(t(opts.lang ?? "es", "n.source_failed", { source: sourceLabel(job.source), error: (r.reason as Error).message }));
    }
  });
  if (!lists.length) throw settled.find((r) => r.status === "rejected")!.reason;
  const total = Object.values(hits).reduce((a, b) => a + b, 0);
  // De todos los candidatos, los mejor puntuados (ver priority.ts)
  return { papers: prioritize(lists, limit), total };
}

export function sourceLabel(source: SourceName): string {
  return { europepmc: "Europe PMC", openalex: "OpenAlex", semanticscholar: "Semantic Scholar", arxiv: "arXiv" }[source];
}

/**
 * Busca con la consulta más estricta y, si da pocos resultados, completa con las
 * más relajadas. Los papers de la consulta estricta van primero.
 */
export async function findPapers(
  api: Api,
  queries: string[],
  limit: number,
  notices: string[],
  opts: SearchOpts = {},
  dedup = new Deduper(),
): Promise<{ query: string; papers: Paper[]; total: number }> {
  const used: string[] = [];
  const papers: Paper[] = [];
  let total = 0;
  for (const query of queries) {
    const found = await fetchMixed(api, query, limit, opts, notices);
    const fresh = found.papers.map((p) => dedup.add(p)).filter((p): p is Paper => p !== null);
    if (fresh.length) {
      used.push(query);
      papers.push(...fresh);
      total = found.total; // las consultas relajadas incluyen a las estrictas
    }
    if (papers.length >= Math.min(limit, MIN_RESULTS)) break;
  }
  if (used.length > 1) {
    notices.push(t(opts.lang ?? "es", "n.few_results", { query: used[0]!, rest: used.slice(1).join(" · ") }));
  } else if (used.length && used[0] !== queries[0]) {
    notices.push(t(opts.lang ?? "es", "n.relaxed", { query: queries[0]! }));
  }
  return { query: used[0] ?? queries[0]!, papers: papers.slice(0, limit), total };
}

function chunks<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

export interface ExhaustiveOptions {
  // Tope de papers analizados en total (rápida + exhaustiva), para acotar el gasto en Jev
  max: number;
  signal?: AbortSignal;
  onProgress?: (p: Progress) => void;
  onUpdate?: (r: AnalysisResult) => void;
}

/**
 * Una búsqueda en dos fases. `quick()` analiza los papers mejor priorizados;
 * `exhaustive()` sigue paginando la consulta hasta agotarla (o hasta el tope),
 * sin reclasificar lo ya analizado.
 */
export class SearchSession {
  private question = "";
  private plan: Plan | null = null;
  private query = "";
  private total = 0;
  private notices: string[] = [];
  private readonly results: PaperResult[] = [];
  // Todo paper visto (clasificado o en cola), por cualquiera de sus identificadores
  private readonly dedup = new Deduper();
  private analyzed = 0;
  private phase: AnalysisResult["phase"] = "quick";
  private exhaustiveDone = false;
  private limitReached = false;
  // Mensaje del servidor si se alcanzó el tope de gasto (#13): la búsqueda se para con lo que haya
  private budgetStop: string | null = null;

  private readonly api: Api;

  constructor(
    api: Api,
    private readonly o: AnalyzeOptions,
  ) {
    // Los avisos de las páginas (p. ej. OpenAlex sin clave) llegan al usuario, una vez cada uno
    const notice = <T extends { notice?: string }>(page: T): T => {
      if (page.notice && !this.notices.includes(page.notice)) this.notices.push(page.notice);
      return page;
    };
    this.api = { ...api, search: async (r) => notice(await api.search(r)), references: async (r) => notice(await api.references(r)) };
  }

  snapshot(): AnalysisResult {
    const verdict = this.results.length ? assess(this.results) : null;
    return {
      question: this.question,
      question_en: this.plan?.question_en ?? null,
      query: this.query,
      backend: this.o.backend,
      total_found: this.total,
      results: sortResults(this.results),
      summary: summarize(this.results),
      verdict,
      plain_summary: plainSummary(verdict, this.results, this.lang, this.plan?.human_focus ?? true),
      notices: this.notices,
      phase: this.phase,
      search_id: this.plan?.search_id ?? null,
      slug: this.plan?.slug ?? null,
      cached: !!this.plan?.cached,
      human_focus: this.plan?.human_focus ?? true,
      exhaustive_done: this.exhaustiveDone,
      limit_reached: this.limitReached,
    };
  }

  /** Carga una búsqueda guardada como si se acabara de hacer; la exhaustiva sigue desde ahí. */
  private async restore(id: string, cached: NonNullable<Plan["cached"]>, progress: Progress): Promise<AnalysisResult> {
    const stored = await this.api.stored!(id);
    for (const r of stored.results) {
      if (!r.classification || this.dedup.has(r.paper)) continue;
      this.dedup.add(r.paper);
      this.results.push(r);
    }
    this.analyzed = this.results.length;
    this.phase = stored.phase;
    this.query = this.plan!.queries[0] ?? "";
    // Total de la consulta, para ofrecer la exhaustiva (una página de Europe PMC, gratis)
    try {
      const page = await this.api.search({ query: this.query, sort: "default", source: "europepmc", page_size: 1, title_abs_only: this.o.titleAbsOnly ?? true });
      this.total = Math.max(page.hit_count, this.analyzed);
    } catch {
      this.total = this.analyzed;
    }
    const day = new Date(cached.created_at).toLocaleDateString(this.lang, { day: "numeric", month: "long", year: "numeric" });
    this.notices.push(t(this.lang, "n.cached", { day, n: this.analyzed }));
    progress.stage = "done";
    progress.found = progress.classified = this.analyzed;
    this.o.onProgress?.({ ...progress });
    return this.snapshot();
  }

  /** Fuentes de esta búsqueda: arXiv solo en las disciplinas donde aporta (#57). */
  private sources(): SourceName[] {
    const all = this.o.sources ?? DEFAULT_SOURCES;
    const field = this.plan?.field;
    return all.filter((s) => s !== "arxiv" || (field != null && ARXIV_FIELDS.has(field)));
  }

  private get lang(): Lang {
    return this.o.lang ?? "es";
  }

  private noticeBudget() {
    if (this.budgetStop && !this.notices.includes(this.budgetStop)) this.notices.push(this.budgetStop);
  }

  /** Papers de la consulta que aún no se han analizado (según el total de la fuente). */
  get remaining(): number {
    return this.exhaustiveDone || this.limitReached ? 0 : Math.max(0, this.total - this.analyzed);
  }

  async quick(): Promise<AnalysisResult> {
    const o = this.o;
    this.question = o.question.trim();
    const progress: Progress = { stage: "planning", found: 0, classified: 0 };
    o.onProgress?.({ ...progress });

    let questionEn: string | undefined;
    if (o.translate && looksSpanish(this.question)) {
      try {
        const en = await o.translate(this.question);
        // Si pierde un concepto clave, mejor que traduzca el Worker
        if (translationKeepsTerms(this.question, en)) questionEn = en;
      } catch {
        // el Worker lo intentará con sus propios traductores
      }
    }
    this.plan = await this.api.plan({ question: this.question, query: o.query?.trim() || undefined, question_en: questionEn, refresh: o.refresh, turnstile_token: await o.turnstileToken?.() });
    this.notices = [...this.plan.notices];
    // Caché (#12): la misma pregunta ya se buscó hace poco → se carga sin volver a pagar Jev
    if (this.plan.cached && this.plan.search_id && this.api.stored) {
      try {
        return await this.restore(this.plan.search_id, this.plan.cached, progress);
      } catch (e) {
        if ((e as Error).name === "AbortError") throw e;
        this.notices.push(t(this.lang, "n.cached_failed"));
      }
    }

    checkAborted(o.signal);
    progress.stage = "searching";
    o.onProgress?.({ ...progress });
    const found = await findPapers(
      this.api,
      this.plan.queries,
      o.limit,
      this.notices,
      { titleAbsOnly: o.titleAbsOnly, pageSize: o.pageSize, signal: o.signal, sources: this.sources(), paceMs: o.paceMs, lang: this.lang },
      this.dedup,
    );
    this.query = found.query;
    this.total = found.total;
    if (!found.papers.length) {
      this.exhaustiveDone = true;
      this.notices.push(t(this.lang, "n.no_papers"));
    }
    progress.found = found.papers.length;
    progress.stage = "classifying";
    o.onProgress?.({ ...progress });
    const queue = chunks(found.papers, o.batchSize ?? 10);
    const worker = async () => {
      for (let batch = queue.shift(); batch && !this.budgetStop; batch = queue.shift()) {
        checkAborted(o.signal);
        await this.classifyBatch(batch, progress, o.onProgress, o.onUpdate);
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, o.concurrency ?? 4) }, worker));
    this.noticeBudget();

    progress.stage = "done";
    o.onProgress?.({ ...progress });
    return this.snapshot();
  }

  async exhaustive(x: ExhaustiveOptions): Promise<AnalysisResult> {
    if (!this.plan) throw new Error("Primero hay que hacer la búsqueda rápida");
    this.phase = "exhaustive";
    const progress: Progress = { stage: "exhaustive", found: this.analyzed, classified: this.results.length };
    x.onProgress?.({ ...progress });

    // Productor: pagina la consulta (primero los más citados) y encola los papers nuevos.
    // Consumidores: clasifican lotes en cuanto hay papers suficientes.
    const pending: Paper[] = [];
    let producing = true;
    let wakers: (() => void)[] = [];
    const notify = () => {
      const w = wakers;
      wakers = [];
      for (const wake of w) wake();
    };
    const room = () => (this.budgetStop ? 0 : x.max - this.analyzed - pending.length);

    // Recorre cada fuente por orden de citas; los papers ya vistos (en cualquier fuente) se saltan
    const pageThrough = async (source: SourceName, query = this.query, cap = Infinity): Promise<"done" | "full"> => {
      let cursor: string | undefined;
      let added = 0;
      const pace = pacer(source, this.o.paceMs);
      while (room() > 0 && added < cap) {
        checkAborted(x.signal);
        await pace();
        const page = await this.api.search({
          query,
          // Primero los más citados, si la fuente lo admite
          sort: SOURCE_SORTS[source][0]!,
          source,
          cursor,
          page_size: this.o.pageSize,
          title_abs_only: this.o.titleAbsOnly ?? true,
        });
        for (const p of page.papers) {
          if (room() <= 0) break;
          if (added >= cap) break;
          // Si ya se vio (en cualquier fuente), solo se fusionan sus datos
          const canonical = this.dedup.add(p);
          if (canonical) {
            pending.push(canonical);
            added++;
          }
        }
        progress.found = this.analyzed + pending.length;
        x.onProgress?.({ ...progress });
        notify();
        if (!page.next_cursor) return "done";
        cursor = page.next_cursor;
      }
      return "full";
    };
    // Snowballing: primero, los estudios que citan las revisiones sistemáticas relevantes
    // (ya hicieron una búsqueda exhaustiva; suelen incluir estudios que la consulta no encuentra)
    const followReferences = async () => {
      const reviews = this.results
        .filter((r) => r.classification?.design === "meta_analysis" && r.classification.answer !== "not_addressed")
        .sort((a, b) => b.classification!.weight - a.classification!.weight)
        .slice(0, SNOWBALL_REVIEWS)
        .map((r) => r.paper);
      if (!reviews.length) return;
      const ids = reviews.map((p) => /^OA:(W\d+)$/.exec(p.source_id)?.[1]).filter((id): id is string => !!id);
      const dois = reviews.filter((p) => !/^OA:W/.test(p.source_id) && p.doi).map((p) => p.doi!);
      let request: ReferencesRequest = { openalex_ids: ids, dois };
      let added = 0;
      while (room() > 0 && added < SNOWBALL_MAX) {
        checkAborted(x.signal);
        const page = await this.api.references(request);
        for (const p of page.papers) {
          if (room() <= 0 || added >= SNOWBALL_MAX) break;
          const canonical = this.dedup.add({ ...p, via: VIA_REVIEW });
          if (canonical) {
            pending.push(canonical);
            added++;
          }
        }
        progress.found = this.analyzed + pending.length;
        x.onProgress?.({ ...progress });
        notify();
        if (!page.next_cursor) return;
        request = { openalex_ids: page.openalex_ids, cursor: page.next_cursor };
      }
    };

    // Pseudo-relevance feedback: temas frecuentes en los papers relevantes (p. ej. "osteoporosis"
    // para la leche y los huesos) que Jev considera útiles → consulta ampliada
    const followTopics = async () => {
      const groups = this.plan?.groups ?? [];
      if (!groups.length) return;
      const topics = frequentTopics(this.results, groups.join(" "));
      if (!topics.length) return;
      const { accepted, assigned } = await this.api.expand({ question: this.question, question_en: this.plan!.question_en, topics, groups });
      if (!accepted.length) return;
      // Cada tema entra como alternativa en el grupo de su concepto; los demás grupos siguen igual
      const expanded = assigned
        ? addToGroups(groups, assigned)
        : `${groups[0]} AND (${accepted.map((t) => (/\s/.test(t) ? `"${t}"` : t)).join(" OR ")})`;
      if (!expanded) return;
      this.notices.push(t(this.lang, "n.expanded", { topics: accepted.join(", ") }));
      let budget = EXPANSION_MAX;
      for (const source of this.sources()) {
        if (budget <= 0 || room() <= 0) break;
        const before = pending.length + this.analyzed;
        try {
          await pageThrough(source, expanded, budget);
        } catch (e) {
          if ((e as Error).name === "AbortError") throw e;
        }
        budget -= pending.length + this.analyzed - before;
      }
    };

    const produce = async () => {
      try {
        try {
          await followReferences();
        } catch (e) {
          if ((e as Error).name === "AbortError") throw e;
          this.notices.push(t(this.lang, "n.refs_failed", { error: (e as Error).message }));
        }
        try {
          await followTopics();
        } catch (e) {
          if ((e as Error).name === "AbortError") throw e;
          this.notices.push(t(this.lang, "n.topics_failed", { error: (e as Error).message }));
        }
        for (const source of this.sources()) {
          let outcome: "done" | "full";
          try {
            outcome = await pageThrough(source);
          } catch (e) {
            if ((e as Error).name === "AbortError") throw e;
            this.notices.push(t(this.lang, "n.exhaustive_source_failed", { source: sourceLabel(source), error: (e as Error).message }));
            continue;
          }
          if (outcome === "full") {
            this.limitReached = true;
            return;
          }
        }
        this.exhaustiveDone = true;
      } finally {
        producing = false;
        notify();
      }
    };

    const batchSize = this.o.batchSize ?? 10;
    const consume = async () => {
      for (;;) {
        checkAborted(x.signal);
        if (this.budgetStop) return;
        if (pending.length >= batchSize || (!producing && pending.length)) {
          await this.classifyBatch(pending.splice(0, batchSize), progress, x.onProgress, x.onUpdate);
        } else if (!producing) {
          return;
        } else {
          await new Promise<void>((resolve) => wakers.push(resolve));
        }
      }
    };

    await Promise.all([produce(), ...Array.from({ length: Math.max(1, this.o.concurrency ?? 4) }, consume)]);
    this.noticeBudget();
    if (this.limitReached && !this.budgetStop) {
      this.notices.push(t(this.lang, "n.exhaustive_limit", { max: x.max, total: num(this.lang, this.total) }));
    }
    progress.stage = "done";
    x.onProgress?.({ ...progress });
    return this.snapshot();
  }

  private async classifyBatch(
    batch: Paper[],
    progress: Progress,
    onProgress?: (p: Progress) => void,
    onUpdate?: (r: AnalysisResult) => void,
  ) {
    this.analyzed += batch.length;
    let batchResults: PaperResult[];
    try {
      const resp = await this.api.classify({
        question: this.question,
        question_en: this.plan!.question_en,
        human_focus: this.plan!.human_focus ?? true,
        field: this.plan!.field ?? null,
        papers: batch,
        search_id: this.plan!.search_id ?? null,
        phase: this.phase,
      });
      batchResults = resp.results;
    } catch (e) {
      if ((e as Error).name === "AbortError") throw e;
      // Tope de gasto: se para la búsqueda sin marcar los papers como erróneos
      if (e instanceof ApiError && e.status === 429) {
        this.budgetStop ??= e.message;
        this.analyzed -= batch.length;
        return;
      }
      // Un lote fallido no tumba la búsqueda: sus papers quedan marcados con error
      batchResults = batch.map((paper) => ({ paper, classification: null, error: (e as Error).message }));
    }
    this.results.push(...batchResults);
    progress.classified = this.results.length;
    onProgress?.({ ...progress });
    onUpdate?.(this.snapshot());
  }
}

/** Fase rápida de una búsqueda (atajo para quien no necesita la exhaustiva). */
export function analyze(api: Api, o: AnalyzeOptions): Promise<AnalysisResult> {
  return new SearchSession(api, o).quick();
}
